/**
 * POS Terminal Embed
 *
 * Renders the user-provided POS billing terminal prototype (billing-pos-vertical-rail.html)
 * EXACTLY as-is via an iframe — zero reinterpretation of its markup/CSS/JS.
 *
 * This single file is intentionally reused, byte-for-byte identical, across FOUR menu
 * entries (POS Billing, Cash Counter Billing, Sales Invoice, Sales Invoice with Returns).
 * Per the user's explicit instruction, all four currently render the identical screen;
 * differentiated backend routing (per transaction type) will be wired later once the
 * user shares the finalized POS/Invoice APIs.
 *
 * "Maximize" behavior (per compliance requirement — 21 CFR Part 11 style controlled
 * session; the billing screen must not be exitable by accident):
 *   - This is an APP-LAYOUT state (`isPosMaximized`, threaded down from App.js), NOT the
 *     real browser Fullscreen API. Engaging it hides the app's Header + Sidebar so the
 *     billing grid gets the reclaimed workspace, while the main Taskbar (login user /
 *     date-time) stays visible and functional at all times.
 *   - Because it's a plain app-state boolean (no `document.requestFullscreen()` call
 *     involved at all), the Escape key has ZERO effect on it — there is nothing for the
 *     browser to natively exit, so it can never be dismissed by accident. The ONLY way
 *     out is the explicit minimize button below.
 *   - Auto-engages the moment one of the 4 POS screens opens, and auto-releases when
 *     navigating away from it (so other screens aren't left in the maximized layout).
 */
import React, { useEffect } from 'react';
import { Minimize2, Maximize2 } from 'lucide-react';

const POS_TERMINAL_URL = '/pos-terminal/billing-pos-vertical-rail.html';

const PosTerminalEmbed = ({ isPosMaximized, setIsPosMaximized }) => {
  useEffect(() => {
    setIsPosMaximized(true);
    return () => setIsPosMaximized(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="relative w-full h-full">
      <iframe
        src={POS_TERMINAL_URL}
        title="RetailCliQ POS Terminal"
        data-testid="pos-terminal-iframe"
        className="w-full h-full border-0 block"
        allow="camera; microphone"
      />
      <button
        type="button"
        data-testid={isPosMaximized ? 'pos-minimize-btn' : 'pos-expand-btn'}
        onClick={() => setIsPosMaximized(!isPosMaximized)}
        title={isPosMaximized ? 'Minimize' : 'Expand workspace'}
        className="absolute top-2 right-2 z-10 flex items-center justify-center w-7 h-7 rounded-md bg-black/60 hover:bg-black/80 text-white transition-colors"
      >
        {isPosMaximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      </button>
    </div>
  );
};

export default PosTerminalEmbed;
